import { Link, NavLink } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, Phone, X } from "lucide-react";
import { SITE } from "@/data/site";

const NAV = [
  { to: "/", label: "Home", end: true },
  { to: "/services", label: "Services" },
  { to: "/service-areas", label: "Service Areas" },
  { to: "/gallery", label: "Gallery" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function SiteHeader() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white/85 backdrop-blur-xl border-b border-slate-200/70 shadow-sm" : "bg-transparent"
      }`}
    >
      {/* Top utility bar */}
      <div className="bg-slate-950 text-white text-[11px] sm:text-xs">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-8 flex items-center justify-center gap-2 tracking-[0.18em] uppercase font-semibold">
          <span className="text-amber-300">Tampa Bay Pool Pros</span>
          <span className="text-slate-400">|</span>
          <span>{SITE.experience} Experience</span>
          <span className="hidden sm:inline text-slate-400">|</span>
          <span className="hidden sm:inline text-emerald-400">24/7 Emergency</span>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-24 sm:h-28 grid grid-cols-3 items-center gap-4">
        {/* Left: desktop nav (mobile keeps this column empty for visual balance) */}
        <nav className="hidden lg:flex items-center gap-7 justify-self-start" data-testid="primary-nav">
          {NAV.slice(0, 3).map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `text-sm font-medium tracking-tight transition-colors ${
                  isActive ? "text-sky-700" : "text-slate-700 hover:text-sky-700"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="lg:hidden" />

        {/* Center: large circular logo */}
        <Link to="/" className="justify-self-center" data-testid="site-logo-link">
          <span className="inline-flex items-center justify-center w-28 h-28 sm:w-32 sm:h-32 rounded-full bg-white shadow-lg ring-4 ring-white border border-slate-200 overflow-hidden">
            <img
              src={SITE.logo}
              alt={`${SITE.name} logo`}
              className="w-full h-full object-contain p-1"
            />
          </span>
        </Link>

        {/* Right: desktop secondary nav + CTAs / mobile hamburger */}
        <div className="justify-self-end flex items-center gap-2 sm:gap-3">
          <nav className="hidden lg:flex items-center gap-7 mr-2">
            {NAV.slice(3).map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `text-sm font-medium tracking-tight transition-colors ${
                    isActive ? "text-sky-700" : "text-slate-700 hover:text-sky-700"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>
          <a
            href={`tel:${SITE.phoneTel}`}
            className="hidden md:inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-900 text-white text-sm font-semibold hover:bg-sky-700 transition-colors whitespace-nowrap"
            data-testid="header-call-button"
          >
            <Phone className="w-4 h-4" />
            {SITE.phoneDisplay}
          </a>
          <a
            href="/contact"
            className="hidden sm:inline-flex items-center px-4 py-2 rounded-full bg-amber-400 text-slate-900 text-sm font-bold hover:bg-amber-300 transition-colors whitespace-nowrap"
            data-testid="header-quote-button"
          >
            Free Quote
          </a>
          <button
            type="button"
            className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-full bg-white/90 border border-slate-200"
            onClick={() => setOpen(true)}
            aria-label="Open menu"
            data-testid="mobile-menu-open"
          >
            <Menu className="w-5 h-5" />
          </button>
        </div>
      </div>

      {open && (
        <div
          className="fixed inset-0 z-[60] bg-white/95 backdrop-blur-xl flex flex-col p-6 lg:hidden"
          data-testid="mobile-menu"
        >
          <div className="flex items-center justify-between">
            <Link to="/" onClick={() => setOpen(false)} className="flex items-center gap-3">
              <img src={SITE.logo} alt={SITE.name} className="h-10 w-auto" />
              <span className="font-heading text-lg font-bold">{SITE.name}</span>
            </Link>
            <button
              type="button"
              className="w-10 h-10 rounded-full bg-slate-100 inline-flex items-center justify-center"
              onClick={() => setOpen(false)}
              aria-label="Close menu"
              data-testid="mobile-menu-close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <nav className="mt-10 flex flex-col items-end gap-4 text-right">
            {NAV.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                onClick={() => setOpen(false)}
                className="text-2xl font-heading font-bold text-slate-900"
                data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {n.label}
              </Link>
            ))}
          </nav>
          <div className="mt-auto grid gap-3">
            <a
              href={`tel:${SITE.phoneTel}`}
              onClick={() => setOpen(false)}
              className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-slate-900 text-white font-semibold"
              data-testid="mobile-call-button"
            >
              <Phone className="w-4 h-4" /> Call {SITE.phoneDisplay}
            </a>
            <a
              href="/contact"
              onClick={() => setOpen(false)}
              className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-amber-400 text-slate-900 font-bold"
              data-testid="mobile-quote-button"
            >
              Get a Free Quote
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
